import * as RadioGroup from "@radix-ui/react-radio-group";
import { Separator } from "@repo/ui/components/ui/separator";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@repo/ui/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useAutoSubmit } from "@/hooks/useAutoSubmit";
import { cn } from "@repo/ui/lib/utils";

const FormOrderSchema = z.object({
  ordering: z.enum(["asc", "desc", "none"], {
    required_error: "You need to select an order type.",
  }),
});

const orderTranslation = {
  asc: { sortByAsc: "minVariantTargetPrice", sortByDesc: "" },
  desc: { sortByDesc: "minVariantTargetPrice", sortByAsc: "" },
  none: { sortByDesc: "minVariantTargetPrice", sortByAsc: "" },
};

export const FiltersMenuItemOrder = ({
  initialValue,
  applyFilter,
  className,
}: {
  applyFilter: (data: { sortByAsc: string; sortByDesc: string }) => void;
  initialValue?: "asc" | "desc" | "none";
  className?: string;
}) => {
  const form = useForm<z.infer<typeof FormOrderSchema>>({
    resolver: zodResolver(FormOrderSchema),
  });

  function onSubmit(data: z.infer<typeof FormOrderSchema>) {
    applyFilter(orderTranslation[data.ordering]);
  }

  useEffect(() => {
    form.setValue("ordering", initialValue || "none");
  }, [initialValue]);

  useAutoSubmit({
    trigger: form.trigger,
    watch: form.watch,
    onSubmit: form.handleSubmit(onSubmit),
  });

  const t = useTranslations();

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <div className={cn("py-[16px]", className)}>
          <AccordionTrigger className="max-md:text-base font-medium py-0 data-[state=open]:text-primary-magenta">
            {t("Products-list.filters.order")}
          </AccordionTrigger>
          <AccordionContent className="mt-[20px] p-0">
            <div className="pb-1 px-0 text-left w-full">
              <Form {...form}>
                <form onChange={form.handleSubmit(onSubmit)}>
                  <FormField
                    control={form.control}
                    name="ordering"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <RadioGroup.Root
                            defaultValue={initialValue}
                            onValueChange={field.onChange}
                            className="flex flex-col"
                            aria-label="Price ordering"
                          >
                            <div className="flex items-center gap-4 py-2">
                              <label
                                className="cursor-pointer flex-grow"
                                style={{
                                  fontWeight:
                                    field.value === "asc" ? "bold" : "normal",
                                }}
                                htmlFor="asc"
                              >
                                {t("Products-list.filters.order-asc")}
                              </label>
                              <RadioGroup.Item
                                className="w-3 h-3 bg-transparent rounded-full"
                                value="asc"
                                id="asc"
                              >
                                <RadioGroup.Indicator className="flex w-full h-full justify-center items-center relative :after:content-[''] after:bg-primary-magenta after:w-3 after:h-3 after:rounded-full" />
                              </RadioGroup.Item>
                            </div>
                            <div className="flex items-center gap-4 py-2">
                              <label
                                className="cursor-pointer flex-grow"
                                style={{
                                  fontWeight:
                                    field.value === "desc" ? "bold" : "normal",
                                }}
                                htmlFor="desc"
                              >
                                {t("Products-list.filters.order-desc")}
                              </label>
                              <RadioGroup.Item
                                className="w-3 h-3 bg-transparent rounded-full"
                                value="desc"
                                id="desc"
                              >
                                <RadioGroup.Indicator className="flex w-full h-full justify-center items-center relative :after:content-[''] after:bg-primary-magenta after:w-3 after:h-3 after:rounded-full" />
                              </RadioGroup.Item>
                            </div>
                          </RadioGroup.Root>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </form>
              </Form>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </Accordion>
  );
};
